'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type MobileCards<T> =
  | {
      /** Below `md`, render each row as a card instead of the table. */
      mobileCard: (row: T) => ReactNode;
      rows: T[];
      rowKey: (row: T) => string;
      /** One short line when `rows` is empty (cards only; the table shows its own). */
      empty?: string;
    }
  | { mobileCard?: undefined; rows?: undefined; rowKey?: undefined; empty?: undefined };

type TableScrollerProps<T> = MobileCards<T> & {
  /** A `<table>` or the `Table` primitive. */
  children: ReactNode;
  className?: string;
};

// The `Table` primitive already scrolls inside its own bordered wrapper, so the
// element that actually overflows may be a direct child rather than this one.
function findScroller(root: HTMLElement): HTMLElement | null {
  const candidates = [root, ...Array.from(root.children)] as HTMLElement[];
  return (
    candidates.find(
      (el) =>
        el.scrollWidth > el.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(el).overflowX)
    ) ?? null
  );
}

/**
 * Horizontal scroll for any table inside its own container, with a faint edge
 * fade on whichever side still has content, so a wide table never widens the
 * page. Pass `mobileCard` + `rows` to swap the table for card-per-row below `md`.
 */
export function TableScroller<T>({ children, className, mobileCard, rows, rowKey, empty }: TableScrollerProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const scroller = findScroller(root);
    if (!scroller) {
      setFade((prev) => (prev.left || prev.right ? { left: false, right: false } : prev));
      return;
    }
    const left = scroller.scrollLeft > 1;
    const right = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
    setFade((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    update();
    const observer = new ResizeObserver(update);
    observer.observe(root);
    root.addEventListener('scroll', update, true);
    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', update, true);
    };
  }, [update]);

  // The fades sit on a wrapper, not inside the scroller: absolute children of a
  // scrolling box travel with its content.
  const edge = 'pointer-events-none absolute inset-y-0 z-10 w-6 transition-opacity';
  return (
    <div className={cn('min-w-0', className)}>
      <div className={cn('relative min-w-0', mobileCard && 'max-md:hidden')}>
        <div ref={rootRef} className="wide-scroll min-w-0 overflow-x-auto">
          {children}
        </div>
        <div aria-hidden className={cn(edge, 'left-0 bg-linear-to-r from-zinc-950/70 to-transparent', fade.left ? 'opacity-100' : 'opacity-0')} />
        <div aria-hidden className={cn(edge, 'right-0 bg-linear-to-l from-zinc-950/70 to-transparent', fade.right ? 'opacity-100' : 'opacity-0')} />
      </div>
      {mobileCard ? (
        <div className="grid gap-2 md:hidden">
          {rows.length === 0 ? (
            <div className="corner-brand border border-zinc-800 bg-zinc-900 px-3 py-8 text-center text-sm text-zinc-500">
              {empty ?? 'Nothing here.'}
            </div>
          ) : (
            rows.map((row) => <div key={rowKey(row)}>{mobileCard(row)}</div>)
          )}
        </div>
      ) : null}
    </div>
  );
}
